import axios from 'axios';
import moment from 'moment';
import logger from '../../utils/logger';

export const getCalendarAPI = (url, userAccessToken, params={}) => {
  const request = {
    headers: { Authorization: `Bearer ${userAccessToken}` },
    ...(params ? ({params: params}) : ({}))
  }
  logger.api('GET:', url);
  return axios.get(url, request);
}

export const postCalendarAPI = (url, userAccessToken, data={}) => {
  const config = {
    headers: { Authorization: `Bearer ${userAccessToken}` },
  }
  logger.api('POST:', url);
  return axios.post(url, data, config);
}

export const putCalendarAPI = (url, userAccessToken, data={}) => {
  const config = {
    headers: { Authorization: `Bearer ${userAccessToken}` },
  }
  logger.api('PUT:', url);
  return axios.put(url, data, config);
}

export const patchCalendarAPI = (url, userAccessToken, data={}, params={}) => {
  const config = {
    headers: { Authorization: `Bearer ${userAccessToken}` },
    params,
  };
  logger.api('PATCH:', url);
  return axios.patch(url, data, config);
}

export const deleteCalendarAPI = (url, userAccessToken) => {
  const config = {
    headers: { Authorization: `Bearer ${userAccessToken}` },
  }
  logger.api('DELETE:', url);
  return axios.delete(url, config);
}

export const listCalendars = (userAccessToken) => {
  return getCalendarAPI('https://www.googleapis.com/calendar/v3/users/me/calendarList',
    userAccessToken);
}

export const listUpcomingEvents = (userAccessToken, calendarId='primary', currentDate) => {
  // Fetch full year of events to support yearly view
  const params = {
    'timeMin': moment(currentDate).startOf('year').toISOString(),
    'timeMax': moment(currentDate).endOf('year').toISOString(),
    'maxResults': 2500, // Increase limit for full year
    'singleEvents': true, // Expand recurring events
  };

  return getCalendarAPI(`https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events`,
    userAccessToken, params);
}

/**
 * List events with incremental sync support using syncToken
 * @param {string} userAccessToken - The OAuth access token
 * @param {string} calendarId - Calendar ID (default 'primary')
 * @param {object} options - Sync options
 * @param {string} options.syncToken - Token for incremental sync (from previous response)
 * @param {Date} options.timeMin - Minimum time bound (required for full sync)
 * @param {Date} options.timeMax - Maximum time bound (optional)
 * @param {boolean} options.showDeleted - Include deleted events (needed for sync)
 * @returns {Promise} - API response with events and nextSyncToken
 */
export const listEventsWithSync = async (userAccessToken, calendarId='primary', options={}) => {
  const { syncToken, timeMin, timeMax, showDeleted = true } = options;

  const params = {};

  if (syncToken) {
    // Incremental sync - only need syncToken
    params.syncToken = syncToken;
  } else {
    // Full sync - need time bounds
    params.timeMin = timeMin ? moment(timeMin).toISOString() : moment().startOf('year').toISOString();
    if (timeMax) {
      params.timeMax = moment(timeMax).toISOString();
    }
  }

  // Always include these for sync
  params.showDeleted = showDeleted;
  params.singleEvents = true;
  params.maxResults = 2500;

  const url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events`;

  try {
    const response = await getCalendarAPI(url, userAccessToken, params);
    return {
      success: true,
      events: response.data.items || [],
      nextSyncToken: response.data.nextSyncToken,
      nextPageToken: response.data.nextPageToken
    };
  } catch (error) {
    // If syncToken is invalid (410 Gone), caller should do full sync
    if (error.response?.status === 410) {
      return {
        success: false,
        error: 'sync_token_invalid',
        message: 'Sync token expired, full sync required'
      };
    }
    logger.error('listEventsWithSync error:', error.response?.status);
    return {
      success: false,
      error: error.response?.data || error.message
    };
  }
}

/**
 * Get a single event by ID (useful for conflict resolution)
 * @param {string} userAccessToken - The OAuth access token
 * @param {string} eventId - Event ID
 * @param {string} calendarId - Calendar ID (default 'primary')
 * @returns {Promise} - Event data
 */
export const getEvent = async (userAccessToken, eventId, calendarId='primary') => {
  const url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events/${encodeURIComponent(eventId)}`;

  try {
    const response = await getCalendarAPI(url, userAccessToken);
    return { success: true, event: response.data };
  } catch (error) {
    logger.error('getEvent error:', error.response?.status);
    return {
      success: false,
      error: error.response?.data || error.message,
      status: error.response?.status
    };
  }
}


export const insertEvent = async (userAccessToken, event, calendarId='primary') => {
  const url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events`;
  logger.api('insertEvent for calendar:', calendarId);
  try {
      const response = await postCalendarAPI(url, userAccessToken, event);
      if ('error' in response){
        logger.error('insertEvent failed');
        return response;
      }
      return response.data;
    } catch (error) {
      logger.error('insertEvent error:', error.response?.status);
      return error.response.data;
    };
}

export const updateEvent = async (userAccessToken, event, calendarId='primary') => {
  if (!('id' in event)){
    logger.error('updateEvent: event missing id');
    return;
  }
  const url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events/${encodeURIComponent(event.id)}`;
  logger.api('updateEvent:', event.id);
  try {
      const response = await putCalendarAPI(url, userAccessToken, event);
      if ('error' in response){
        logger.error('updateEvent failed');
        return response;
      }
      return response.data;
    } catch (error) {
      logger.error('updateEvent error:', error.response?.status);
      return error.response.data;
    };
}

export const deleteEvent = async (userAccessToken, eventId, calendarId='primary') => {
  const url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events/${encodeURIComponent(eventId)}`;
  logger.api('deleteEvent:', eventId);
  try {
    const response = await deleteCalendarAPI(url, userAccessToken);
    logger.api('deleteEvent success');
    return { success: true, data: response.data };
  } catch (error) {
    logger.error('deleteEvent failed:', error.response?.status);
    return { success: false, error: error.response?.data || error.message };
  }
}

/**
 * Patch an event's attendees on Google Calendar.
 * Uses PATCH to update only the attendees field without overwriting other data.
 *
 * @param {string} userAccessToken - OAuth access token
 * @param {string} eventId - Google Calendar event ID
 * @param {string} calendarId - Google Calendar ID
 * @param {Array<{email: string, responseStatus: string, displayName?: string}>} attendees
 * @param {string} sendUpdates - 'all' | 'externalOnly' | 'none' (controls invitation emails)
 * @returns {Promise<{success: boolean, data?: Object, error?: any, status?: number}>}
 */
export const patchEventAttendees = async (userAccessToken, eventId, calendarId = 'primary', attendees, sendUpdates = 'all') => {
  const url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events/${encodeURIComponent(eventId)}`;
  logger.api('patchEventAttendees:', eventId, `(${attendees.length} attendees)`);
  try {
    const response = await patchCalendarAPI(url, userAccessToken, { attendees }, { sendUpdates });
    logger.api('patchEventAttendees success');
    return { success: true, data: response.data };
  } catch (error) {
    logger.error('patchEventAttendees error:', error.response?.status);
    return {
      success: false,
      error: error.response?.data || error.message,
      status: error.response?.status,
    };
  }
}
